import type { TeammateId } from "@repo/app-types/entities/teammate";
import {
  DEFAULT_CALLING_RETRY_POLICY,
  type CallingHours,
  type CallingRetryPolicy,
  type TeammateAgentAutoEnrollment,
} from "@repo/app-types/entities/teammateAgent";
import { FieldDescription } from "@repo/ui/field/fieldDescription";
import { FieldError } from "@repo/ui/field/fieldError";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import { AlertDialogVariant } from "@repo/ui/Modal/alertDialog";
import { ConfirmationModal } from "@repo/ui/Modal/confirmationModal";
import { Switch } from "@repo/ui/Switch/switch";
import { TooltipHoverTarget } from "@repo/ui/tooltip/hoverTarget";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import { useState, type FC } from "react";
import { Link } from "react-router";
import { appPaths } from "../../../app/appPaths.js";
import { AutoEnrollmentSection } from "./autoEnrollmentSection.js";
import { CallingHoursEditor } from "./callingHoursEditor.js";

/** Where the Call Task API is documented. */
const CALL_TASK_API_DOCS_URL =
  "https://dev.alpharun.com/api-reference/endpoint/create-call-task";

export interface OutboundCallsSectionProps {
  /** Whether the agent may place calls at all. Applies immediately. */
  isOutboundEnabled: boolean;
  hours: CallingHours;
  retry: CallingRetryPolicy;
  /** IANA zone the calling hours are expressed in (the company timezone). */
  timezone: string;
  /** Set while outbound is on but no day has a window. */
  callingHoursError: string | null;
  /** Outbound calling needs a number to dial from. */
  hasPhoneNumber: boolean;
  isDisabled?: boolean;
  /** The agent runs its call tasks itself, texting and calling by its outreach flow. */
  isAgentManaged: boolean;
  /** Whether the company may switch agent-managed outreach on (feature flag). */
  canEnableAgentManaged: boolean;
  onOutboundEnabledChange: (isEnabled: boolean) => void;
  onHoursChange: (hours: CallingHours) => void;
  onRetryChange: (retry: CallingRetryPolicy) => void;
  allowReenrollments: boolean;
  onAllowReenrollmentsChange: (allowReenrollments: boolean) => void;
  onAgentManagedChange: (isAgentManaged: boolean) => void;
  /** The agent being edited. */
  teammateAgentId: TeammateId;
  /** Enrolling contacts from teammate calls; only shown for an agent-managed agent. */
  autoEnrollment: TeammateAgentAutoEnrollment | undefined;
  onAutoEnrollmentChange: (autoEnrollment: TeammateAgentAutoEnrollment) => void;
}

/**
 * When and how often the agent dials out. The on/off switch is the kill switch
 * and takes effect immediately; the hours and retry policy below it are staged
 * on the draft like the rest of the agent's configuration.
 */
export const OutboundCallsSection: FC<OutboundCallsSectionProps> = (props) => {
  const {
    isOutboundEnabled,
    hours,
    retry,
    timezone,
    callingHoursError,
    hasPhoneNumber,
    isDisabled,
    isAgentManaged,
    canEnableAgentManaged,
    onOutboundEnabledChange,
    onHoursChange,
    onRetryChange,
    allowReenrollments,
    onAllowReenrollmentsChange,
    onAgentManagedChange,
    teammateAgentId,
    autoEnrollment,
    onAutoEnrollmentChange,
  } = props;

  const [isDisableConfirmationOpen, setIsDisableConfirmationOpen] =
    useState(false);

  // Enabling is harmless; disabling strands scheduled calls, so it asks first.
  const handleEnabledChange = (isEnabled: boolean) => {
    if (isEnabled) {
      onOutboundEnabledChange(true);
      return;
    }
    setIsDisableConfirmationOpen(true);
  };

  const areRetriesEnabled = retry.type !== "no_retry";

  // A number is required to turn calling ON, but never to turn it OFF: an
  // agent can lose its number while enabled, and the kill switch has to stay
  // operable in that state rather than locking in the "on" position.
  const canToggle = hasPhoneNumber || isOutboundEnabled;

  const outboundSwitch = (
    <Switch
      isSelected={isOutboundEnabled}
      onChange={handleEnabledChange}
      isDisabled={isDisabled || !canToggle}
      accessibleLabel={
        isOutboundEnabled ? "Disable outbound calls" : "Enable outbound calls"
      }
    />
  );

  return (
    <section
      aria-labelledby="outbound-calls-heading"
      className="border-t border-border-light pt-8"
    >
      <div className="flex items-start justify-between gap-6">
        <div>
          <h2
            id="outbound-calls-heading"
            className="text-base font-semibold text-foreground"
          >
            Outbound calls
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Calls are scheduled by your system through the{" "}
            <a
              href={CALL_TASK_API_DOCS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-indigo-600 hover:text-indigo-500"
            >
              Call Task API
            </a>
            .
          </p>
        </div>

        {/* Only the switch that can't be operated explains itself. A tooltip
            needs a trigger that consumes React Aria's focusable context, which
            `Switch` does not — `TooltipHoverTarget` is what supplies it, and
            wrapping a live switch in that focusable `role="button"` span to
            get a tooltip would nest one control inside another. An enabled
            agent that has lost its number goes without the hint for now. */}
        {canToggle ? (
          outboundSwitch
        ) : (
          <TooltipTrigger>
            <TooltipHoverTarget shape="rounded">
              {outboundSwitch}
            </TooltipHoverTarget>
            <SimpleTooltip>
              This agent needs a phone number before it can call out.
            </SimpleTooltip>
          </TooltipTrigger>
        )}
      </div>

      {isOutboundEnabled ? (
        <div className="mt-6 space-y-6">
          <div className="flex items-start justify-between gap-6">
            <div>
              <Label size={Size.SMALL}>Allow re-enrollment</Label>
              <FieldDescription className="mt-1">
                When enabled, contacts can be enrolled in outreach from this
                agent multiple times.
              </FieldDescription>
            </div>
            <Switch
              isSelected={allowReenrollments}
              onChange={onAllowReenrollmentsChange}
              isDisabled={isDisabled}
              accessibleLabel="Allow re-enrollment"
            />
          </div>
          <div>
            <Label size={Size.SMALL}>Calling hours</Label>
            <p className="mt-1 text-sm text-muted-foreground">
              Outbound calls only start during these windows. Requests outside
              them move to the next open window.{" "}
              <Link
                to={appPaths.settingsOrganization.build()}
                className="font-medium whitespace-nowrap text-indigo-600 hover:text-indigo-500"
              >
                Go to company settings to change timezone.
              </Link>
            </p>

            <div className="mt-3">
              <CallingHoursEditor
                hours={hours}
                timezone={timezone}
                isDisabled={isDisabled}
                onChange={onHoursChange}
              />
            </div>

            {callingHoursError ? (
              <FieldError className="mt-2">{callingHoursError}</FieldError>
            ) : null}
          </div>

          {canEnableAgentManaged || isAgentManaged ? (
            <div className="flex items-start justify-between gap-6">
              <div>
                <Label size={Size.SMALL}>Agent-managed outreach</Label>
                <p className="mt-1 text-sm text-muted-foreground">
                  The agent decides on its own whether to text, call, wait or
                  stop for each task, following its Outreach flow. Retries do
                  not apply: after a call, the agent decides what comes next.
                </p>
              </div>
              <Switch
                isSelected={isAgentManaged}
                onChange={onAgentManagedChange}
                isDisabled={isDisabled}
                accessibleLabel={
                  isAgentManaged
                    ? "Disable agent-managed outreach"
                    : "Enable agent-managed outreach"
                }
              />
            </div>
          ) : null}

          {isAgentManaged ? (
            <AutoEnrollmentSection
              teammateAgentId={teammateAgentId}
              autoEnrollment={autoEnrollment}
              isDisabled={isDisabled ?? false}
              onChange={onAutoEnrollmentChange}
            />
          ) : (
            <div className="flex items-start justify-between gap-6">
              <div>
                <Label size={Size.SMALL}>Retry unanswered calls</Label>
                <p className="mt-1 text-sm text-muted-foreground">
                  Up to 3 attempts total. Retry 4 hours after the first call,
                  then again the next day. If a retry falls outside calling
                  hours, it moves to the next open window.
                </p>
              </div>
              <Switch
                isSelected={areRetriesEnabled}
                onChange={(isSelected) =>
                  onRetryChange(
                    isSelected
                      ? DEFAULT_CALLING_RETRY_POLICY
                      : { type: "no_retry" },
                  )
                }
                isDisabled={isDisabled}
                accessibleLabel={
                  areRetriesEnabled
                    ? "Disable call retries"
                    : "Enable call retries"
                }
              />
            </div>
          )}
        </div>
      ) : null}

      <ConfirmationModal
        isOpen={isDisableConfirmationOpen}
        onOpenChange={setIsDisableConfirmationOpen}
        title="Disable outbound calling?"
        confirmText="Disable outbound"
        cancelText="Keep enabled"
        variant={AlertDialogVariant.Warning}
        onConfirm={() => {
          onOutboundEnabledChange(false);
          setIsDisableConfirmationOpen(false);
        }}
      >
        Calls in progress will finish. Scheduled calls will only go out if you
        re-enable outbound calling before their scheduled time. Otherwise,
        they&apos;re canceled.
      </ConfirmationModal>
    </section>
  );
};
